import React, { Fragment, useCallback, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useDispatch } from "react-redux";
import {
  any,
  append,
  clone,
  concat,
  either,
  equals,
  filter,
  find,
  head,
  isEmpty,
  length,
  map,
  mergeRight,
  omit,
  pipe,
  propEq,
  reject,
  uniqBy,
  values,
} from "ramda";
import { logOut } from "../../../auth/authActions";
import {
  normalizeAppliedFacets,
  normalizeFacetResults,
} from "../../../browse-media/redux/browseMediaHelpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSquareX } from "@fortawesome/pro-regular-svg-icons";
import { useMediaTable } from "../../common/use-mediaTable";

import ContentTable from "./ContentTable";
import ContentTotalsHeader from "./ContentTotalsHeader";
import ContentTypeFilters from "./ContentTypeFilters";
import ContentSearchBar from "./ContentSearchBar";
import EditSearchModal from "../../common/EditSearchModal";
import { isBlank, notBlank } from "../../../util/ext";
import { getTotalSelectedTerms } from "../../contentUtils";
import AddMediaToParentDropDown from "../../common/AddMediaToParentDropdown";
import MediaBrowser from "../../common/MediaBrowser";
import ContentFacetMenu from "./ContentFacetMenu";
import FacetPills from "../../common/FacetPills";
import QueryPills from "../../common/QueryPills";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import Tooltip from "@material-ui/core/Tooltip";

import { useSearchQuery } from "../../common/use-searchQuery";
import { useSnackAlert } from "../../../common/use-snackAlert";
import SnackAlert from "../../../components/SnackAlert";
import { initialStatus } from "../ContentView";
import { Checkbox, FormControlLabel } from "@material-ui/core";

const useStyles = makeStyles(theme => ({
  icon: {
    "& > :first-child": {
      fontSize: "1.4rem",
    },
  },

  select: {
    fontWeight: 700,
    color: "black",
  },

  showDeleted: {
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
}));

// remove (-status:* AND *:*) as option for facet menu
const getStatusTerms = appliedFacets => {
  const status = find(propEq("name", "status"), appliedFacets);
  const terms = status?.terms;
  return terms ?? [];
};

const ContentBrowser = props => {
  const {
    searchState,
    facetState,
    onSearchStateChange,
    onFacetStateChange,
    searchResults,
    facetResults,
    onSaveSuccess,
    setSearchPreferences,
  } = props;

  const classes = useStyles();

  const [contentList, setContentList] = useState([]);
  useEffect(() => {
    if (searchResults.isSuccess) {
      setContentList(searchResults.data.media);
    }
  }, [searchResults.isSuccess, searchResults.data]);

  const [scrollVisible, setScrollVisible] = useState(true);
  const [contentSelected, setContentSelected] = useState([]);
  const [selectedFacets, setSelectedFacets] = useState({});

  const reduxDispatch = useDispatch();

  //-- Manage the alert message
  const snackAlertHandler = useSnackAlert();
  const { showAlert } = snackAlertHandler;

  //-- Handle Search state
  const initialSearchState = clone(searchState);
  const searchQuery = useSearchQuery(initialSearchState);

  //-- Whenever the internal search searchQuery.state changes, update the search searchQuery.state stored in the parent
  useEffect(() => {
    onSearchStateChange(searchQuery.state);
    setScrollVisible(true);
  }, [onSearchStateChange, setScrollVisible, searchQuery.state]);

  useEffect(() => {
    const selectedFacets = omit(
      ["type", "contentType", "omniType", "status", "q"],
      normalizeAppliedFacets(searchQuery.state.appliedFacets),
    );
    setSelectedFacets(selectedFacets);
  }, [searchQuery.state.appliedFacets]);

  //-- Handle Facet state
  const initialFacetState = clone(facetState);
  const facetQuery = useSearchQuery(initialFacetState);
  useEffect(() => {
    onFacetStateChange(facetQuery.state);
  }, [onFacetStateChange, facetQuery.state]);

  //-- Manage the content type facets
  const [contentTypes, setContentTypes] = useState([]);
  const [contentTypeTerms, setContentTypeTerms] = useState([]);
  useEffect(() => {
    if (facetResults.isError) {
      //-- Send user to login if the token has expired or not logged in
      if (facetResults.error.status === 401) {
        reduxDispatch(logOut());
      }
    }

    if (facetResults.isSuccess && notBlank(facetResults.data.facets)) {
      //-- Get the content type facets
      const facet = facetResults.data.facets.contentType;
      const contentTypesFacet = head(normalizeFacetResults([facet]));
      setContentTypes(contentTypesFacet);
      if (contentTypesFacet) {
        //-- Get any previously applied content type facets
        const appliedFacet = find(
          propEq("name", contentTypesFacet.name),
          searchQuery.state.appliedFacets,
        );
        if (appliedFacet) {
          setContentTypeTerms(appliedFacet.terms);
        }
      }
    }
  }, [
    facetResults.isError,
    facetResults.isSuccess,
    facetResults.data,
    facetResults.error,
    searchQuery.state,
    reduxDispatch,
  ]);

  //-- Manage other filters, i.e. not content type
  const [otherAvailableFilters, setOtherAvailableFilters] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({});
  useEffect(() => {
    if (facetResults.isSuccess && notBlank(facetResults.data.facets)) {
      const facets = facetResults.data.facets;

      //-- remove all type facets
      const normalizedFacets = reject(
        ({ name }) => any(equals(name), ["type", "contentType", "omniType"]),
        normalizeFacetResults(values(facets)),
      );

      setOtherAvailableFilters(normalizedFacets);
    }
  }, [facetResults.isSuccess, facetResults.data]);

  //-- Event handlers
  const [totalSelectedTerms, setTotalSelectedTerms] = useState(0);

  const handleFacetsSelected = useCallback(
    newSelectedFacet => {
      const prevSelectedFilters = omit(
        ["type", "contentType", "omniType"],
        normalizeAppliedFacets(searchQuery.state.appliedFacets),
      );

      //-- remove facet with empty terms list
      let selectedFilters;
      if (isEmpty(newSelectedFacet.terms)) {
        const { name } = newSelectedFacet;
        const prevFacets = prevSelectedFilters;
        delete prevFacets[name];
        selectedFilters = prevFacets;
      } else {
        selectedFilters = {
          ...prevSelectedFilters,
          [newSelectedFacet.name]: newSelectedFacet,
        };
      }

      //-- Update filter button count
      const termCount = getTotalSelectedTerms(selectedFilters);
      setTotalSelectedTerms(termCount);

      //-- Keep only facets for type and content type
      const initialFacetState = filter(
        either(propEq("name", "type"), propEq("name", "contentType")),
        searchQuery.state.appliedFacets,
      );

      //-- Add newly selected facets
      const facets = concat(values(selectedFilters), initialFacetState);
      searchQuery.handleUpdateFacets(facets);
      facetQuery.handleUpdateFacets(facets);
    },
    [searchQuery, facetQuery],
  );

  const handleClearFilters = useCallback(() => {
    setTotalSelectedTerms(0);

    //-- Keep only facet for type="CONTENT" and reset to initial status filter
    const initialFacetState = pipe(
      filter(propEq("name", "type")),
      append(initialStatus),
    )(searchQuery.state.appliedFacets);

    searchQuery.handleUpdateFacets(initialFacetState);
    facetQuery.handleUpdateFacets(initialFacetState);
  }, [searchQuery, facetQuery]);

  const handleClearFacetPill = useCallback(
    facet => {
      const updatedFacets = reject(
        propEq("name", facet.name),
        searchQuery.state.appliedFacets,
      );

      // update selected filter list for filter drop down menu
      const updatedSelectedFilters = omit([facet.name], selectedFilters);
      setSelectedFilters(updatedSelectedFilters);

      // update selected filter count for filter drop down menu
      setTotalSelectedTerms(getTotalSelectedTerms(updatedSelectedFilters));

      // update searchQuery.state.appliedFacets
      searchQuery.handleUpdateFacets(updatedFacets);
      facetQuery.handleUpdateFacets(updatedFacets);
    },
    [searchQuery, facetQuery, selectedFilters, setSelectedFilters],
  );

  const handleStatusClick = status => event => {
    const checked = event.target.checked;

    // Toggle status
    const updatedFacets = map(facet => {
      if (facet.name === "status") {
        let terms = !checked
          ? reject(propEq("code", status), facet.terms)
          : append({ code: status }, facet.terms);
        return { ...facet, terms: uniqBy(t => t.code, terms) };
      }

      return facet;
    }, searchQuery.state.appliedFacets);

    // update searchQuery.state.appliedFacets
    searchQuery.handleUpdateFacets(updatedFacets);
    facetQuery.handleUpdateFacets(updatedFacets);
  };

  //-- Manage nested menu items
  const [selectedFacet, setSelectedFacet] = useState("");
  const handleFacetClick = facetName => {
    equals(selectedFacet, facetName)
      ? setSelectedFacet("")
      : setSelectedFacet(facetName);
  };

  const handleFacetPillClick = facetName => {
    setSelectedFacet(facetName);
  };

  const handleContentTypeSelected = useCallback(
    term => {
      //-- If the term already exists in the list then remove it; if not then add it
      const terms = any(propEq("label", term.label), contentTypeTerms)
        ? reject(propEq("label", term.label), contentTypeTerms)
        : append(term, contentTypeTerms);

      //-- Build the new facet with the updated terms
      const facet = {
        name: contentTypes.name,
        queryParam: contentTypes.queryParam,
        terms: terms,
      };

      //-- Remove the old facet and add in the new one
      const facets = append(
        facet,
        reject(
          propEq("name", contentTypes.name),
          searchQuery.state.appliedFacets,
        ),
      );
      searchQuery.handleUpdateFacets(facets);
      facetQuery.handleUpdateFacets(facets);
    },
    [contentTypeTerms, contentTypes, searchQuery, facetQuery],
  );

  const handleClearContentType = useCallback(() => {
    //-- Clear out the content type facet
    setContentTypeTerms([]);
    const facets = reject(
      propEq("name", contentTypes.name),
      searchQuery.state.appliedFacets,
    );
    searchQuery.handleUpdateFacets(facets);
    facetQuery.handleUpdateFacets(facets);
  }, [searchQuery, facetQuery, contentTypes]);

  const handleScrollVisible = useCallback(visible => {
    setScrollVisible(visible);
  }, []);

  const [disableAddToSubcollection, setDisableAddToSubcollection] = useState(
    true,
  );

  const handleSelectChange = useCallback(selected => {
    setDisableAddToSubcollection(isEmpty(selected));
    setContentSelected(selected);
  }, []);

  const handleFilterDelete = useCallback(
    value => {
      searchQuery.handleUpdateQuery(
        reject(equals(value), searchQuery.state.appliedQuery),
      );
      facetQuery.handleUpdateQuery(
        reject(equals(value), searchQuery.state.appliedQuery),
      );
    },
    [searchQuery, facetQuery],
  );

  const handleUpdateQuery = useCallback(
    query => {
      searchQuery.handleUpdateQuery(query);
      facetQuery.handleUpdateQuery(query);
    },
    [searchQuery, facetQuery],
  );

  const handleUpdateTitleOnly = useCallback(
    event => {
      const checked = event.target.checked;
      searchQuery.handleUpdateTitleOnly(checked);
      facetQuery.handleUpdateTitleOnly(checked);

      setSearchPreferences(prev => ({ ...prev, titleOnly: checked }));
    },
    [searchQuery, facetQuery, setSearchPreferences],
  );

  //-- Replacing Search Term in Query Pill
  const [editSearchModal, setEditSearchModal] = useState(false);
  const [previousSearchTerm, setPreviousSearchTerm] = useState("");

  const handleEditSearchModalClose = () => setEditSearchModal(false);

  const handleQueryPillClick = queryPart => {
    setEditSearchModal(true);
    setPreviousSearchTerm(queryPart);
  };

  const handleEditSearchApply = useCallback(
    newSearchTerm => {
      const appliedQuery = searchQuery.state.appliedQuery;

      const withoutPreviousTerm = reject(
        equals(previousSearchTerm),
        appliedQuery,
      );

      const updatedQuery = isBlank(newSearchTerm)
        ? withoutPreviousTerm
        : append(newSearchTerm, withoutPreviousTerm);

      searchQuery.handleUpdateQuery(updatedQuery);
      facetQuery.handleUpdateQuery(updatedQuery);

      handleEditSearchModalClose();
      setPreviousSearchTerm("");
    },
    [previousSearchTerm, searchQuery, facetQuery],
  );

  // Manage Selected Content
  const {
    state,
    handleSelectAllContent,
    handleRowClick,
    handleRemoveAllSelected,
  } = useMediaTable(
    { onSelectChange: handleSelectChange },
    { selected: contentSelected },
  );

  // Status
  const statusTerms = getStatusTerms(searchQuery.state.appliedFacets);

  return (
    <Fragment>
      <SnackAlert handler={snackAlertHandler} />

      <EditSearchModal
        editSearchModal={editSearchModal}
        previousSearchTerm={previousSearchTerm}
        handleEditSearchModalClose={handleEditSearchModalClose}
        handleEditSearchApply={handleEditSearchApply}
      />

      <MediaBrowser
        loaded={searchResults.isSuccess}
        scrollVisible={scrollVisible}
        searchBar={
          <Grid container style={{ padding: "0 15px" }}>
            <Grid container item alignItems="center">
              <ContentTotalsHeader
                count={
                  searchResults.isSuccess ? searchResults.data.totalCount : "-"
                }
              />
              <ContentTypeFilters
                loading={facetResults.isLoading}
                contentTypes={contentTypes}
                contentTypeTerms={contentTypeTerms}
                onContentTypeSelected={handleContentTypeSelected}
                onClear={handleClearContentType}
              />
              <Grid item>
                <ContentSearchBar
                  query={searchQuery.state.appliedQuery}
                  onQueryChange={handleUpdateQuery}
                  filterMenu={
                    <Grid container justify="center" alignItems="center">
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="titleOnly"
                            color="primary"
                            checked={searchQuery.state.titleOnly}
                            onChange={handleUpdateTitleOnly}
                          />
                        }
                        label="Title Only"
                      />
                      <ContentFacetMenu
                        facets={map(
                          facet => {
                            const isSelected = Boolean(
                              selectedFacets[facet.name],
                            );
                            const selectedTerms =
                              facet.name === "status"
                                ? statusTerms
                                : isSelected
                                ? selectedFacets[facet.name].terms
                                : [];
                            return mergeRight(facet, {
                              selected: isSelected,
                              selectedTerms: selectedTerms,
                            });
                          },
                          append(
                            // manually add status
                            {
                              name: "status",
                              displayName: "Status",
                            },
                            otherAvailableFilters,
                          ),
                        )}
                        handleClearFilters={handleClearFilters}
                        handleFacetClick={handleFacetClick}
                        handleFacetSelection={handleFacetsSelected}
                        handleStatusClick={handleStatusClick}
                        loading={facetResults.isLoading}
                        selectedFacet={selectedFacet}
                        selectedFacets={selectedFacets}
                        totalSelectedTerms={totalSelectedTerms}
                      />
                    </Grid>
                  }
                />
              </Grid>
            </Grid>
            <Grid container item alignItems="center">
              <Grid item>
                <QueryPills
                  handleClick={handleQueryPillClick}
                  appliedQuery={searchQuery.state.appliedQuery}
                  handleDelete={handleFilterDelete}
                />
                <FacetPills
                  facetPills={selectedFacets}
                  handleClearFacet={handleClearFacetPill}
                  handleClick={handleFacetPillClick}
                />
              </Grid>
              <Grid item container alignItems="center" justify="flex-end">
                {!isEmpty(contentSelected) && (
                  <Grid item style={{ marginRight: "auto" }}>
                    <Tooltip title="Remove All Selected" placement="bottom">
                      <Button
                        onClick={() => handleRemoveAllSelected()}
                        startIcon={<FontAwesomeIcon icon={faSquareX} />}
                        classes={{
                          root: classes.select,
                          iconSizeMedium: classes.icon,
                        }}
                      >
                        {" " + length(contentSelected) + " selected"}
                      </Button>
                    </Tooltip>
                  </Grid>
                )}

                <Grid item>
                  <AddMediaToParentDropDown
                    parent="collection"
                    disableButton={disableAddToSubcollection}
                    childList={contentSelected}
                    showAlert={showAlert}
                    handleAddSuccess={onSaveSuccess}
                  />
                </Grid>
                <Grid item>
                  <AddMediaToParentDropDown
                    parent="module"
                    disableButton={disableAddToSubcollection}
                    childList={contentSelected}
                    showAlert={showAlert}
                    handleAddSuccess={onSaveSuccess}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        }
        mediaTable={
          <ContentTable
            contentList={contentList}
            total={searchResults.isSuccess ? searchResults.data.totalCount : 0}
            page={searchQuery.state.page}
            orderBy={searchQuery.state.orderBy}
            order={searchQuery.state.orderDirection}
            rowsPerPage={searchQuery.state.limit}
            onSortChange={searchQuery.handleUpdateSort}
            onPageChange={searchQuery.handleUpdatePage}
            onRowsPerPageChange={searchQuery.handleUpdateLimit}
            onSelectChange={handleSelectChange}
            onSelectAllContent={handleSelectAllContent}
            onRowClick={handleRowClick}
            searchState={searchQuery.state}
            selected={state.selected}
            onScrollVisible={handleScrollVisible}
          />
        }
      />
    </Fragment>
  );
};

export default ContentBrowser;
